<template>
  <div id="user-edit-tab-info">
    <div class="vx-row">
      <div class="vx-col w-full">
        <div class="vx-col w-full mb-base">
          <vs-progress
            v-if="isLoadingRecords"
            indeterminate
            color="primary"
            class="mb-4"
          />
          <div v-if="loadError" class="hours-rendered-tab-error mb-4">
            <span>Overtime records could not be loaded.</span>
            <vs-button type="border" size="small" @click="loadOvertimeData">
              Try again
            </vs-button>
          </div>
          <div
            v-if="!isLoadingRecords && !loadError"
            class="hours-rendered-record-switch mb-4"
          >
            <button
              type="button"
              :class="{ active: recordView === 'active' }"
              @click="recordView = 'active'"
            >
              <feather-icon icon="CheckCircleIcon" svgClasses="h-4 w-4" />
              Current Active
              <span>{{ activeOvertime.length }}</span>
            </button>
            <button
              type="button"
              :class="{ active: recordView === 'history' }"
              @click="recordView = 'history'"
            >
              <feather-icon icon="ClockIcon" svgClasses="h-4 w-4" />
              History
              <span>{{ overtimeHistory.length }}</span>
            </button>
          </div>
          <template>
            <vs-table
              v-if="!isLoadingRecords && sortedOvertimeData.length"
              stripe
              max-items="4"
              pagination
              search
              :data="sortedOvertimeData"
            >
              <template slot="thead">
                <vs-th @click.native="sort('overtimeTypeId')">Type</vs-th>
                <vs-th @click.native="sort('overtimeHours')">OT Hours</vs-th>
                <vs-th @click.native="sort('otRate')">OT Rate</vs-th>
                <!-- removed NSD Rate column as per payroll's request. Can be added back later if needed.
                <vs-th @click.native="sort('nsdRate')">NSD Rate?</vs-th> -->
                <vs-th @click.native="sort('hourlyRate')">Hourly Rate</vs-th>
                <vs-th>Action</vs-th>
              </template>
              <template slot-scope="{ data }">
                <vs-tr :data="tr" :key="indextr" v-for="(tr, indextr) in data">
                  <vs-td :data="data[indextr].overtimeTypeId">
                    {{ overtimeTypeLabel(data[indextr].overtimeTypeId) }}
                  </vs-td>
                  <vs-td :data="data[indextr].overtimeHours">
                    {{ data[indextr].overtimeHours }}
                  </vs-td>
                  <vs-td :data="data[indextr].otRate">
                    {{ data[indextr].otRate }}
                  </vs-td>
                  <!-- removed NSD Rate column as per payroll's request. Can be added back later if needed.
                  <vs-td :data="data[indextr].nsdRate">
                    {{ data[indextr].nsdRate }}
                  </vs-td> -->
                  <vs-td :data="data[indextr].hourlyRate">
                    {{ data[indextr].hourlyRate }}
                  </vs-td>
                  <vs-td>
                    <div :style="{ direction: $vs.rtl ? 'rtl' : 'ltr' }">
                      <feather-icon
                        icon="Edit3Icon"
                        svgClasses="h-5 w-5 mr-4 hover:text-primary cursor-pointer"
                        @click="editRecord(data[indextr].id)"
                      />
                      <!-- TODO: Add delete after record-deletion rules and permissions are finalized. -->
                    </div>
                  </vs-td>
                </vs-tr>
              </template>
            </vs-table>
          </template>
          <div
            v-if="!isLoadingRecords && !loadError && !sortedOvertimeData.length"
            class="hours-rendered-record-empty"
          >
            <feather-icon
              :icon="recordView === 'active' ? 'CheckCircleIcon' : 'ClockIcon'"
              svgClasses="h-8 w-8"
            />
            <h4>{{ recordView === "active" ? "No active overtime" : "No overtime history" }}</h4>
            <p>
              {{
                recordView === "active"
                  ? "Add overtime for this employee to get started."
                  : "Closed-payroll overtime will appear here."
              }}
            </p>
          </div>
        </div>
      </div>
    </div>
    <div class="vx-row">
      <div class="vx-col w-full">
        <vs-button
          v-if="recordView === 'active'"
          type="gradient"
          color="primary"
          :disabled="isLoadingRecords || loadError"
          @click="promptAddNewovertime()"
          icon-pack="feather"
          icon="icon-plus"
          >Add</vs-button
        >
      </div>
    </div>

    <!-- ADD overtime -->
    <vs-prompt
      class="email-compose"
      title="Add Overtime"
      accept-text="Submit"
      @accept="onAddRecord"
      :is-valid="validAddForm"
      :active.sync="activePromptAddRecord"
    >
      <vx-card title="You may update the following information">
        <div v-if="isLoadingSalary" class="hours-rendered-inline-loading mb-3">
          <vs-progress indeterminate color="primary" class="mb-2" />
          Loading the employee's hourly rate…
        </div>
        <vs-alert
          v-if="salaryLoadError"
          :active="true"
          color="warning"
          class="mb-3"
        >
          The hourly rate could not be loaded. Close this form and try again.
        </vs-alert>
        <div class="vx-row mb-3">
          <div class="vx-col sm:w-2/2 md:w-2/2 lg:w-2/2 xl:w-2/2 w-full mb-2">
            <span class="text-sm">Payroll Type</span>
            <span style="color: red">*</span>
            <v-select
              name="payroll type"
              v-validate="'required'"
              class="w-full"
              v-model="createPayroll"
              label="createPayrollId"
              :options="getAllActiveCreatePayrollId"
              placeholder="Please Select"
            />
            <span
              class="text-danger text-sm"
              v-show="errors.has('payroll type')"
              >{{ errors.first("payroll type") }}</span
            >
          </div>
        </div>

        <div class="vx-row mb-3">
          <div class="vx-col sm:w-2/2 md:w-2/2 lg:w-2/2 xl:w-2/2 w-full mb-2">
            <span class="text-sm">Overtime Type</span>
            <span style="color: red">*</span>
            <v-select
              name="overtime type"
              v-validate="'required'"
              class="w-full"
              v-model="overtimeType"
              label="overtimeTypeName"
              :options="getAllOvertimeTypeLookup"
              placeholder="Please Select"
            />
            <span
              class="text-danger text-sm"
              v-show="errors.has('overtime type')"
              >{{ errors.first("overtime type") }}</span
            >
          </div>
        </div>
        <div class="vx-row mb-3">
          <div class="vx-col sm:w-2/2 md:w-2/2 lg:w-2/2 xl:w-2/2 w-full mb-2">
            <span class="text-sm">OT Hours</span
            ><span style="color: red">*</span>
            <vs-input
              name="ot hours"
              v-validate="'max_value:1000'"
              class="w-full"
              v-model="overtimeHours"
            />
            <span class="text-danger text-sm" v-show="errors.has('ot hours')">{{
              errors.first("ot hours")
            }}</span>
          </div>
        </div>
      </vx-card>
    </vs-prompt>
    <!-- END ADD overtime -->
    <!-- EDIT overtime -->
    <vs-prompt
      class="email-compose"
      title="Edit overtime"
      accept-text="Submit"
      @accept="onUpdateRecord"
      :is-valid="validEditForm"
      :active.sync="activePromptEditRecord"
    >
      <vx-card title="You may update the following information">
        <div class="vx-row mb-3">
          <div class="vx-col sm:w-2/2 md:w-2/2 lg:w-2/2 xl:w-2/2 w-full mb-2">
            <span class="text-sm">Overtime Type</span>
            <span style="color: red">*</span>
            <v-select
              name="overtime type"
              v-validate="'required'"
              class="w-full"
              v-model="overtimeType"
              label="overtimeTypeName"
              :options="getAllOvertimeTypeLookup"
              placeholder="Please Select"
            />
            <span
              class="text-danger text-sm"
              v-show="errors.has('overtime type')"
              >{{ errors.first("overtime type") }}</span
            >
          </div>
        </div>
        <div class="vx-row mb-3">
          <div class="vx-col sm:w-2/2 md:w-2/2 lg:w-2/2 xl:w-2/2 w-full mb-2">
            <span class="text-sm">OT Hours</span
            ><span style="color: red">*</span>
            <vs-input
              name="ot hours"
              v-validate="'max_value:1000'"
              class="w-full"
              v-model="overtimeHours"
            />
            <span class="text-danger text-sm" v-show="errors.has('ot hours')">{{
              errors.first("ot hours")
            }}</span>
          </div>
        </div>
      </vx-card>
    </vs-prompt>
    <!-- END EDIT overtime -->
  </div>
</template>
<script>
import moment from "moment";
import vSelect from "vue-select";
export default {
  props: {
    paymentMode: {
      type: Number,
      required: true,
    },
    userId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      selected: [],

      overtimeId: 0,
      overtimeType: [],
      overtimeHours: 0,
      nsdRate: 0,
      otRate: 0,
      hourlyRate: 0,
      isManuallyAdded: false,
      addedManuallyOn: null,
      addedManuallyBy: "",
      uploadedOn: null,
      uploadedBy: "",
      extractedOn: null,
      extractedBy: "",
      createPayrollId: 0,

      createPayroll: null,

      activePromptAddRecord: false,
      activePromptEditRecord: false,
      isLoadingRecords: false,
      loadError: false,
      isLoadingSalary: false,
      salaryLoadError: false,
      recordView: "active",

      configdateTimePicker: {
        allowInput: true,
      },

      sortKey: "",
      sortOrder: 1, //1 ascending, -1 Descending
    };
  },
  components: {
    "v-select": vSelect,
  },
  computed: {
    getAllOvertime() {
      return this.$store.getters["payroll/getOvertimeByUserId"](this.userId);
    },
    getAllOvertimeTypeLookup() {
      return this.$store.state.lookup.overtimeTypes;
    },
    getAllPeriodByPaymentMode() {
      return this.$store.getters["lookup/periodLookupByPaymentMode"](
        this.paymentMode
      );
    },
    overtimeTypeName() {
      return (id) => this.$store.getters["lookup/overtimeType"](id);
    },
    getAllActiveCreatePayrollId() {
      return this.$store.getters["payroll/getAllActiveCreatePayrollId"]();
    },
    periodName() {
      return (id) => this.$store.getters["lookup/periodLookup"](id);
    },
    getCreatePayrollTypeName() {
      return (id) => this.$store.getters["lookup/createPayrollTypeIdById"](id);
    },
    validAddForm() {
      return Boolean(
        this.createPayroll &&
          this.createPayroll.createPayrollId &&
          this.overtimeType &&
          this.overtimeType.overtimeTypeId &&
          this.overtimeHours !== "" &&
          this.overtimeHours !== null &&
          Number(this.overtimeHours) > 0 &&
          !this.isLoadingSalary &&
          !this.salaryLoadError
      );
    },
    validEditForm() {
      return Boolean(
        this.overtimeId &&
          this.overtimeType &&
          this.overtimeType.overtimeTypeId &&
          this.overtimeHours !== "" &&
          this.overtimeHours !== null &&
          Number(this.overtimeHours) > 0
      );
    },
    getCurrentDate() {
      return moment(new Date()).format("YYYY-MM-DD");
    },
    activeOvertime() {
      const payrolls = this.$store.state.payroll.getAllCreatePayroll || [];
      return this.getAllOvertime.filter((overtime) => {
        const payroll = payrolls.find(
          (item) => item.createPayrollId === overtime.createPayrollId
        );
        return !payroll || !payroll.isClosed;
      });
    },
    overtimeHistory() {
      const payrolls = this.$store.state.payroll.getAllCreatePayroll || [];
      return this.getAllOvertime.filter((overtime) => {
        const payroll = payrolls.find(
          (item) => item.createPayrollId === overtime.createPayrollId
        );
        return Boolean(payroll && payroll.isClosed);
      });
    },
    displayedOvertime() {
      return this.recordView === "history"
        ? this.overtimeHistory
        : this.activeOvertime;
    },

    sortedOvertimeData() {
      if (this.sortKey) {
        const sortedData = this.displayedOvertime.slice().sort((a, b) => {
          let aValue = a[this.sortKey];
          let bValue = b[this.sortKey];

          // Handle undefined or null values
          if (aValue == null) return this.sortOrder;
          if (bValue == null) return -this.sortOrder;

          // Compare numbers or strings
          if (typeof aValue === "number" && typeof bValue === "number") {
            return this.sortOrder * (aValue - bValue);
          }
          return this.sortOrder * aValue.localeCompare(bValue);
        });

        return sortedData;
      } else {
        return this.displayedOvertime;
      }
    },
  },
  methods: {
    loadOvertimeData() {
      return this.$store.dispatch("payroll/preloadHoursRenderedData", {
        force: true,
      });
    },
    overtimeTypeLabel(id) {
      const overtimeType = this.overtimeTypeName(id);
      return overtimeType ? overtimeType.overtimeTypeName : "Loading…";
    },
    promptAddNewovertime() {
      this.resetOvertimeForm();
      this.getEmployeeSalary();
      this.activePromptAddRecord = true;
    },
    resetOvertimeForm() {
      this.overtimeId = 0;
      this.overtimeType = [];
      this.overtimeHours = 0;
      this.nsdRate = 0;
      this.otRate = 0;
      this.hourlyRate = 0;
      this.isManuallyAdded = false;
      this.addedManuallyOn = null;
      this.addedManuallyBy = "";
      this.uploadedOn = null;
      this.uploadedBy = "";
      this.extractedOn = null;
      this.extractedBy = "";
      this.createPayrollId = 0;
      this.createPayroll = null;
      this.isLoadingSalary = false;
      this.salaryLoadError = false;
      this.$nextTick(() => this.$validator.reset());
    },
    getEmployeeSalary() {
      this.isLoadingSalary = true;
      this.salaryLoadError = false;
      this.$store
        .dispatch("hrms/getAllSalary", this.userId)
        .then((response) => {
          const salaries = response.data && response.data.data;
          const salary = salaries && salaries[0];
          if (!salary) throw new Error("No salary record was found.");
          this.hourlyRate = salary.hourlyRate || 0;
          this.isLoadingSalary = false;
        })
        .catch(() => {
          this.isLoadingSalary = false;
          this.salaryLoadError = true;
        });
    },
    editRecord(id) {
      const e = this.$store.getters["payroll/getOvertimeById"](id);

      this.overtimeId = e.id;
      (this.createPayrollId = e.createPayrollId),
        (this.overtimeType = this.overtimeTypeName(e.overtimeTypeId)),
        (this.overtimeHours = e.overtimeHours),
        (this.otRate = e.otRate),
        (this.nsdRate = e.nsdRate),
        (this.hourlyRate = e.hourlyRate),
        (this.isManuallyAdded = e.isManuallyAdded),
        (this.addedManuallyOn = e.addedManuallyOn),
        (this.addedManuallyBy = e.addedManuallyBy),
        (this.uploadedOn = e.uploadedOn),
        (this.uploadedBy = e.uploadedBy),
        (this.extractedOn = e.extractedOn),
        (this.extractedBy = e.extractedBy),
        (this.activePromptEditRecord = true);
    },
    onAddRecord() {
      this.$vs.loading();

      const e = {
        createPayrollId: this.createPayroll.createPayrollId,
        overtimeTypeId: this.overtimeType.overtimeTypeId,
        overtimeHours: this.overtimeHours,
        otRate: this.overtimeTypeName(this.overtimeType.overtimeTypeId).otRate,
        nsdRate: this.overtimeTypeName(this.overtimeType.overtimeTypeId)
          .nsdRate,
        hourlyRate: this.hourlyRate,
        isManuallyAdded: true,
        addedManuallyOn: this.getCurrentDate,
        addedManuallyBy: this.$store.state.AppActiveUser.uid,
        uploadedOn: this.uploadedOn,
        uploadedBy: this.uploadedBy,
        extractedOn: this.extractedOn,
        extractedBy: this.extractedBy,
        userId: this.userId,
        currentUserId: this.$store.state.AppActiveUser.uid,
      };
      this.$store
        .dispatch("payroll/AddEmployeeOvertime", e)
        .then(() => {
          this.$vs.loading.close();
        })
        .catch((error) => {
          this.handleSaveError(error);
        });
    },
    onUpdateRecord() {
      this.$vs.loading();
      const e = {
        createPayrollId: this.createPayrollId,
        overtimeTypeId: this.overtimeType.overtimeTypeId,
        overtimeHours: this.overtimeHours,
        otRate: this.otRate,
        nsdRate: this.nsdRate,
        hourlyRate: this.hourlyRate,
        isManuallyAdded: this.isManuallyAdded,
        addedManuallyOn: this.addedManuallyOn,
        addedManuallyBy: this.addedManuallyBy,
        uploadedOn: this.uploadedOn,
        uploadedBy: this.uploadedBy,
        extractedOn: this.extractedOn,
        extractedBy: this.extractedBy,
        userId: this.userId,
        id: this.overtimeId,
        currentUserId: this.$store.state.AppActiveUser.uid,
      };
      this.$store
        .dispatch("payroll/UpdateEmployeeOvertime", e)
        .then(() => {
          this.$vs.loading.close();
          this.$vs.notify({
            title: "Success",
            text: "Overtime Updated",
            iconPack: "feather",
            icon: "icon-alert-circle",
            color: "primary",
          });
        })
        .catch((error) => {
          this.$vs.loading.close();

          this.$vs.notify({
            title: "Error",
            text: error.message,
            iconPack: "feather",
            icon: "icon-x-octagon",
            color: "danger",
          });
        });
    },
    handleSaveError(error) {
      this.$vs.loading.close();
      this.$vs.notify({
        title: "Unable to save",
        text: error && error.message ? error.message : "Please try again.",
        iconPack: "feather",
        icon: "icon-x-octagon",
        color: "danger",
      });
    },
    sort(key) {
      if (this.sortKey === key) {
        this.sortOrder *= -1;
      } else {
        this.sortKey = key;
        this.sortOrder = 1;
      }
    },
  },
};
</script>
